import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowPathIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import CellSaveStatusIndicator from "../../controls/CellSaveStatusIndicator";
import InlineSelectCell from "../../controls/InlineSelectCell";
import InfoPopover from "../../controls/InfoPopover";
import SummaryDiscountInput from "./SummaryDiscountInput";
import SummaryTaxIncludedCheckbox from "./SummaryTaxIncludedCheckbox";
import OrderPricingNumberInput from "./OrderPricingNumberInput";
import { useCellSaveState } from "../../../hooks/useCellSaveState";
import {
  convertToCurrencyFormat,
  enrichTaxesWithDropdownRates,
  getSourceItemUnitLabel,
  isManagementUser,
  isReadOnlyUser,
  sumSourceItemTaxRates,
} from "../../../shared/utility";
import { promotionRuleSummary } from "../../../shared/promotionFormatting";
import {
  buildPluPriceGroups,
  estimateGroupSubtotal,
  parsePriceDraft,
} from "./pluPriceGroups";

function reconstructedEditor(group) {
  return {
    unitPrice: group.baseUnitPrice == null ? "" : String(group.baseUnitPrice),
    tiers: group.tiers.map((tier) => ({
      quantity: formatQuantity(tier.quantity, group.sellingUnit),
      unitPrice: String(tier.unitPrice),
    })),
    saved: false,
  };
}

function formatQuantity(value, sellingUnit) {
  const digits = parseInt(sellingUnit, 10) === 3 ? 0 : 2;
  return Number(value || 0).toFixed(digits);
}

function priceKey(price) {
  return Number(price).toFixed(4);
}

function draftMatchesGroup(draft, group) {
  const parsed = parsePriceDraft(group.totalQuantity, draft.unitPrice, draft.tiers);
  if (!parsed.ok || group.hasPartialSplits || group.baseUnitPrice == null) {
    return false;
  }
  if (priceKey(group.baseUnitPrice) !== priceKey(parsed.unitPrice)) {
    return false;
  }
  if (group.tiers.length !== parsed.tiers.length) {
    return false;
  }
  return group.tiers.every(
    (tier, index) =>
      Math.abs(tier.quantity - parsed.tiers[index].quantity) <= 0.0001 &&
      priceKey(tier.unitPrice) === priceKey(parsed.tiers[index].unit_price),
  );
}

export default function OrderPluSummaryTable(props) {
  const {
    values,
    closed,
    onSavePluPriceGroup,
    onSavePluTaxGroup,
    onUpdateSummaryDiscount,
    onBulkUpdateSourceItemTaxIncluded,
    pricingLocked = false,
    onSetProductPromotionBypass,
    onRefreshPromotions,
    promotionRefreshLocked = false,
    promotionBypassLocked = false,
    showErrorModal,
    taxRateOptions = [],
  } = props;
  const promotionPricingAvailable = Boolean(values?.promotion_pricing_enabled);
  const groups = useMemo(
    () => buildPluPriceGroups(values?.mixed_cartons, values?.promotion_pricing_groups, values?.promotion_pricing_enabled),
    [values?.mixed_cartons, values?.promotion_pricing_groups, values?.promotion_pricing_enabled],
  );
  const allTaxIncludedItems = useMemo(() => ({
    key: "all-items",
    sourceItems: Array.from(new Map(
      groups.flatMap((group) => group.sourceItems).map((item) => [item.id, item]),
    ).values()),
  }), [groups]);
  const [taxIncludedSaving, setTaxIncludedSaving] = useState(false);
  const taxIncludedPending = useRef(false);
  const saveTaxIncluded = async (sourceItemIds, taxIncluded) => {
    if (taxIncludedPending.current) return { success: false, data: { message: "A tax update is already in progress." } };
    taxIncludedPending.current = true;
    setTaxIncludedSaving(true);
    try {
      return await onBulkUpdateSourceItemTaxIncluded(sourceItemIds, taxIncluded);
    } finally {
      taxIncludedPending.current = false;
      setTaxIncludedSaving(false);
    }
  };
  const [drafts, setDrafts] = useState({});
  const [pendingPromotionProducts, setPendingPromotionProducts] = useState({});
  const [promotionRefresh, setPromotionRefresh] = useState({ loading: false, error: null });
  const promotionRefreshInProgress = useRef(false);
  const draftsRef = useRef({});
  const committedPayload = useRef({});
  const { getSaveStatus, saveCell } = useCellSaveState();
  const pendingFocus = useRef(null);
  const togglePromotionBypass = async (productId, bypassed) => {
    if (!onSetProductPromotionBypass || promotionRefreshInProgress.current || pendingPromotionProducts[productId] !== undefined) return;
    setPendingPromotionProducts((current) => ({ ...current, [productId]: bypassed }));
    try {
      await onSetProductPromotionBypass(productId, bypassed);
    } finally {
      setPendingPromotionProducts((current) => {
        const next = { ...current };
        delete next[productId];
        return next;
      });
    }
  };
  const refreshPromotions = async () => {
    if (!onRefreshPromotions || !promotionPricingAvailable || closed || promotionRefreshLocked || promotionRefreshInProgress.current || Object.keys(pendingPromotionProducts).length > 0) return;
    promotionRefreshInProgress.current = true;
    setPromotionRefresh({ loading: true, error: null });
    try {
      const result = await onRefreshPromotions();
      setPromotionRefresh({
        loading: false,
        error: result?.success ? null : result?.message || "Could not refresh promotions. Try again.",
      });
    } catch (error) {
      setPromotionRefresh({ loading: false, error: error?.message || "Could not refresh promotions. Try again." });
    } finally {
      promotionRefreshInProgress.current = false;
    }
  };
  const isManagement = isManagementUser();
  const canEdit =
    isManagement &&
    !isReadOnlyUser() &&
    !closed &&
    typeof onSavePluPriceGroup === "function";
  const canEditTaxes =
    isManagement &&
    !isReadOnlyUser() &&
    !closed &&
    typeof onSavePluTaxGroup === "function";
  const canEditSummaryDiscount = !pricingLocked && typeof onUpdateSummaryDiscount === "function";
  const canEditSummaryTaxIncluded = !closed && !pricingLocked && typeof onBulkUpdateSourceItemTaxIncluded === "function";

  const showActions = canEdit;

  useEffect(() => {
    setDrafts((current) => {
      let changed = false;
      const next = { ...current };
      groups.forEach((group) => {
        const draft = next[group.key];
        if (draft?.saved && draftMatchesGroup(draft, group)) {
          delete next[group.key];
          delete committedPayload.current[group.key];
          changed = true;
        }
      });
      if (changed) {
        draftsRef.current = next;
      }
      return changed ? next : current;
    });
  }, [groups]);

  useEffect(() => {
    if (!pendingFocus.current) {
      return;
    }
    const node = document.getElementById(pendingFocus.current);
    node?.focus();
    pendingFocus.current = null;
  }, [drafts]);

  const editorFor = (group) => drafts[group.key] ?? reconstructedEditor(group);

  const updateDraft = (group, updater) => {
    const current = draftsRef.current;
    const existing = current[group.key] ?? reconstructedEditor(group);
    const nextEditor = updater({ ...existing, saved: false });
    const next = {
      ...current,
      [group.key]: nextEditor,
    };
    draftsRef.current = next;
    const parsed = parsePriceDraft(
      group.totalQuantity,
      nextEditor.unitPrice,
      nextEditor.tiers,
    );
    const signature = parsed.ok
      ? JSON.stringify({
          unit_price: parsed.unitPrice,
          tiers: parsed.tiers,
        })
      : null;
    if (committedPayload.current[group.key] !== signature) {
      delete committedPayload.current[group.key];
    }
    setDrafts(next);
  };

  const commitGroup = (group, reportErrors) => {
    const editor = draftsRef.current[group.key] ?? reconstructedEditor(group);
    const hasDraft = Boolean(draftsRef.current[group.key]);
    if (!hasDraft && !reportErrors) {
      return;
    }

    const parsed = parsePriceDraft(
      group.totalQuantity,
      editor.unitPrice,
      editor.tiers,
    );

    if (!parsed.ok) {
      if (reportErrors) {
        showErrorModal?.("Check the price", parsed.message);
      }
      return;
    }

    if (draftMatchesGroup(editor, group)) {
      return;
    }

    const signature = JSON.stringify({
      unit_price: parsed.unitPrice,
      tiers: parsed.tiers,
    });
    if (committedPayload.current[group.key] === signature) {
      return;
    }
    committedPayload.current[group.key] = signature;

    saveCell(group.key, async () => {
      const result = await onSavePluPriceGroup({
        ...(group.usesProductIdentity ? { product_id: group.productId } : {}),
        plu: group.plu,
        selling_unit_of_measure: group.sellingUnit,
        unit_price: parsed.unitPrice,
        tiers: parsed.tiers,
      });

      if (result?.success) {
        setDrafts((current) => {
          const next = {
            ...current,
            [group.key]: {
              ...(current[group.key] ?? editor),
              saved: true,
            },
          };
          draftsRef.current = next;
          return next;
        });
      } else if (committedPayload.current[group.key] === signature) {
        delete committedPayload.current[group.key];
      }

      return result ?? { success: false };
    });
  };

  const onFieldKeyDown = (event, group) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitGroup(group, true);
    }
  };

  const onFieldBlur = (group) => {
    commitGroup(group, false);
  };

  const saveTaxGroup = (group, nextTaxes) => {
    saveCell(`${group.key}:taxes`, () =>
      onSavePluTaxGroup({
        ...(group.usesProductIdentity ? { product_id: group.productId } : {}),
        plu: group.plu,
        selling_unit_of_measure: group.sellingUnit,
        taxes: (nextTaxes ?? []).map((tax) => ({ value: tax.value })),
      }),
    );
  };

  const addTier = (group) => {
    const nextIndex = editorFor(group).tiers.length;
    pendingFocus.current = `tier-quantity-${group.key}-${nextIndex}`;
    updateDraft(group, (editor) => ({
      ...editor,
      tiers: [...editor.tiers, { quantity: "", unitPrice: "" }],
    }));
  };

  const subtotalFor = (group, editor) => {
    if (!drafts[group.key]) {
      return group.subtotalPrice;
    }
    return (
      estimateGroupSubtotal(
        group.totalQuantity,
        editor.unitPrice,
        editor.tiers,
      ) ??
      group.subtotalPrice
    );
  };

  return (
    <div>
      {groups.length === 0 ? (
        <p className="px-2 py-4 text-sm text-gray-600">
          No cartons on this shipment.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-300 text-sm">
            <thead>
              <tr className="text-left text-gray-900">
                <th scope="col" className="px-2 py-2 font-semibold">
                  Product
                </th>
                <th scope="col" className="px-2 py-2 text-right font-semibold">
                  Quantity
                </th>
                <th scope="col" className="px-2 py-2 font-semibold">
                  Unit
                </th>
                <th scope="col" aria-label="Promotion" className="px-2 py-2 font-semibold">
                  <span className="inline-flex items-center gap-1">
                    Promotion
                    <InfoPopover label="Promotion">
                      Promotions come from the product’s promotion settings. Click a promotion name to remove it for that product in this shipment. Click “Promo removed” to restore it.
                    </InfoPopover>
                    {onRefreshPromotions ? (
                      <button
                        type="button"
                        aria-label={promotionRefresh.error ? "Retry promotion refresh" : "Refresh promotions"}
                        aria-busy={promotionRefresh.loading}
                        title={!promotionPricingAvailable ? "Promotion pricing is unavailable for this shipment" : (closed || promotionRefreshLocked) ? "Promotion pricing is locked for this shipment" : promotionRefresh.error || "Refresh promotions for all products"}
                        disabled={!promotionPricingAvailable || closed || promotionRefreshLocked || promotionRefresh.loading || Object.keys(pendingPromotionProducts).length > 0}
                        onClick={refreshPromotions}
                        className="inline-flex min-h-11 min-w-11 items-center justify-center rounded text-indigo-700 hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:text-gray-400 motion-safe:transition-colors md:min-h-9 md:min-w-9"
                      >
                        <ArrowPathIcon className={`h-4 w-4 ${promotionRefresh.loading ? "motion-safe:animate-spin" : ""}`} aria-hidden="true" />
                      </button>
                    ) : null}
                  </span>
                  {promotionRefresh.error ? <span role="alert" className="mt-1 block max-w-48 text-xs font-normal text-red-700">{promotionRefresh.error}</span> : null}
                </th>
                <th scope="col" aria-label="Discount" className="px-2 py-2 font-semibold">
                  <span className={`inline-flex items-center gap-1 ${canEditSummaryDiscount ? "pl-12 md:pl-10" : ""}`}>
                    Discount
                    <InfoPopover label="Discount">
                      Click $ or % beside the discount input to switch between a dollar amount and a percentage discount.
                    </InfoPopover>
                  </span>
                </th>
                {isManagement ? (
                  <>
                    <th
                      scope="col"
                      className="px-2 py-2 text-right font-semibold"
                    >
                      Unit price
                    </th>
                    <th
                      scope="col"
                      className="px-2 py-2 text-right font-semibold"
                    >
                      Subtotal
                    </th>
                  </>
                ) : null}
                <th scope="col" className="px-2 py-2 font-semibold">
                  <div className="flex items-center gap-1">
                    <span>Tax included</span>
                    <SummaryTaxIncludedCheckbox
                      key={values?.id}
                      group={allTaxIncludedItems}
                      productLabel="all items"
                      compact
                      disabled={!canEditSummaryTaxIncluded || taxIncludedSaving || groups.some((group) => getSaveStatus(`${group.key}:taxes`) === "saving")}
                      onCommit={saveTaxIncluded}
                    />
                  </div>
                </th>
                {isManagement ? (
                  <>
                    <th scope="col" className="px-2 py-2 font-semibold">
                      Tax rates
                    </th>
                  </>
                ) : null}
                {showActions ? (
                  <th scope="col" className="px-2 py-2 text-center font-semibold">
                    <span className="sr-only">Pricing actions</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {groups.map((group) => {
                const editor = editorFor(group);
                const unitLabel =
                  getSourceItemUnitLabel(group.sellingUnit) || "";
                const productLabel = group.productName || "Product";
                const saving = getSaveStatus(group.key) === "saving";

                return (
                  <GroupRows
                    key={group.key}
                    group={group}
                    editor={editor}
                    unitLabel={unitLabel}
                    productLabel={productLabel}
                    saving={saving}
                    canEdit={canEdit}
                    showActions={showActions}
                    isManagement={isManagement}
                    canEditSummaryDiscount={canEditSummaryDiscount}
                    canEditSummaryTaxIncluded={canEditSummaryTaxIncluded && !taxIncludedSaving}
                    onUpdateSummaryDiscount={onUpdateSummaryDiscount}
                    onBulkUpdateSourceItemTaxIncluded={saveTaxIncluded}
                    saveStatus={getSaveStatus(group.key)}
                    taxSaveStatus={getSaveStatus(`${group.key}:taxes`)}
                    taxRateOptions={taxRateOptions}
                    canEditTaxes={canEditTaxes}
                    promotionPricingEnabled={Boolean(values?.promotion_pricing_enabled)}
                    onSetProductPromotionBypass={onSetProductPromotionBypass ? togglePromotionBypass : undefined}
                    promotionRefreshLoading={promotionRefresh.loading}
                    promotionBypassSaving={pendingPromotionProducts[group.productId] !== undefined}
                    promotionBypassTarget={pendingPromotionProducts[group.productId]}
                    closed={closed}
                    promotionBypassLocked={promotionBypassLocked}
                    onSaveTaxes={(nextTaxes) => saveTaxGroup(group, nextTaxes)}
                    subtotal={subtotalFor(group, editor)}
                    onFieldBlur={onFieldBlur}
                    onFieldKeyDown={onFieldKeyDown}
                    onUnitPriceChange={(value) =>
                      updateDraft(group, (current) => ({
                        ...current,
                        unitPrice: value,
                      }))
                    }
                    onTierChange={(index, field, value) =>
                      updateDraft(group, (current) => ({
                        ...current,
                        tiers: current.tiers.map((tier, tierIndex) =>
                          tierIndex === index
                            ? { ...tier, [field]: value }
                            : tier,
                        ),
                      }))
                    }
                    onAddTier={() => addTier(group)}
                    onRemoveTier={(index) =>
                      updateDraft(group, (current) => ({
                        ...current,
                        tiers: current.tiers.filter(
                          (_, tierIndex) => tierIndex !== index,
                        ),
                      }))
                    }
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <OrderTotals values={values} groups={groups} isManagement={isManagement} />
    </div>
  );
}

function TaxRatesCell({
  group,
  productLabel,
  taxRateOptions,
  canEditTaxes,
  saveStatus,
  onCommit,
}) {
  const taxes = enrichTaxesWithDropdownRates(group.taxes || [], taxRateOptions);
  const totalRate = sumSourceItemTaxRates(taxes);

  if (canEditTaxes) {
    return (
      <div className="min-w-56">
        <InlineSelectCell
          ariaLabel={`Tax rates for ${productLabel}`}
          isClearable={true}
          isMulti={true}
          name={`tax_rates_${group.key}`}
          onCommit={onCommit}
          options={taxRateOptions}
          placeholder={group.taxesDiffer ? "Mixed taxes" : "No tax"}
          saveStatus={saveStatus}
          value={group.taxesDiffer ? [] : taxes.map((tax) => tax.value)}
        />
        {group.taxesDiffer ? (
          <p className="mt-1 text-xs text-gray-600">
            Taxes differ on this PLU. Choosing here applies to every item.
          </p>
        ) : null}
      </div>
    );
  }

  if (group.taxesDiffer) {
    return <span className="text-xs text-gray-600">Mixed taxes</span>;
  }

  return (
    <div className="space-y-0.5">
      {taxes.length === 0 ? (
        <span className="text-xs text-gray-400">No tax</span>
      ) : (
        taxes.map((tax) => (
          <div key={tax.value} className="text-xs whitespace-nowrap">
            {tax.label} ({parseFloat(tax.rate).toFixed(2)}%)
          </div>
        ))
      )}
      {taxes.length > 1 ? (
        <div className="text-xs text-gray-500">
          Total: {totalRate.toFixed(2)}%
        </div>
      ) : null}
    </div>
  );
}

function GroupRows({
  group,
  editor,
  unitLabel,
  productLabel,
  saving,
  canEdit,
  showActions,
  isManagement,
  saveStatus,
  taxSaveStatus,
  canEditSummaryDiscount,
  canEditSummaryTaxIncluded,
  onUpdateSummaryDiscount,
  onBulkUpdateSourceItemTaxIncluded,
  taxRateOptions,
  canEditTaxes,
  promotionPricingEnabled,
  onSetProductPromotionBypass,
  promotionBypassSaving,
  promotionRefreshLoading,
  promotionBypassTarget,
  closed,
  promotionBypassLocked,
  onSaveTaxes,
  subtotal,
  onFieldBlur,
  onFieldKeyDown,
  onUnitPriceChange,
  onTierChange,
  onAddTier,
  onRemoveTier,
}) {
  const claimedQuantity = editor.tiers.reduce((sum, tier) => {
    const quantity = Number(tier.quantity);
    return sum + (Number.isFinite(quantity) && quantity > 0 ? quantity : 0);
  }, 0);
  const baseQuantity = Math.max(0, group.totalQuantity - claimedQuantity);
  const promotionPricing = promotionPricingEnabled ? group.promotionPricing : null;
  const promotionLocked = closed || promotionBypassLocked || promotionRefreshLoading;
  const showPromotion = promotionPricing && (promotionPricing.applied_promotion || promotionPricing.promotion_bypassed || promotionBypassSaving);
  const promotionLabel = promotionBypassSaving
    ? (promotionBypassTarget ? "Removing…" : "Restoring…")
    : (promotionPricing?.promotion_bypassed ? "Promo removed" : promotionPricing?.applied_promotion?.name);

  return (
    <>
      <tr>
        <td className="px-2 py-2 align-top">
          <div>
            {productLabel}
            <br />
            <span className="font-bold">PLU: {group.plu}</span>
          </div>
          {group.hasPartialSplits ? (
            <p className="mt-1 text-xs text-gray-600">
              These prices do not line up as one base price plus extra
              quantities. Saving here replaces them.
            </p>
          ) : null}
        </td>
        <td className="px-2 py-2 text-right align-top tabular-nums">
          {formatQuantity(group.totalQuantity, group.sellingUnit)}
        </td>
        <td className="px-2 py-2 align-top whitespace-nowrap">{unitLabel}</td>
        <td className="px-2 py-2 align-top">
          {showPromotion ? (
            <div className="space-y-1">
              {onSetProductPromotionBypass && group.productId ? (
                <button
                  type="button"
                  className="inline-flex min-h-11 items-center gap-1 border-0 bg-transparent p-0 text-left text-sm text-indigo-700 hover:underline focus-visible:rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:text-gray-500 disabled:no-underline md:min-h-9"
                  disabled={promotionLocked || promotionBypassSaving}
                  title={promotionLocked
                    ? "Promotion pricing is locked for this shipment"
                    : `${promotionPricing.promotion_bypassed ? "Restore" : "Remove"} promotion for ${productLabel}`}
                  aria-busy={promotionBypassSaving}
                  onClick={() => onSetProductPromotionBypass(group.productId, !promotionPricing.promotion_bypassed)}
                >
                  {promotionLabel}
                  {promotionBypassSaving ? <span aria-hidden="true"><CellSaveStatusIndicator status="saving" /></span> : null}
                </button>
              ) : <span className="text-sm text-gray-700">{promotionLabel}</span>}
              {promotionPricing.applied_promotion && !promotionPricing.promotion_bypassed ? (
                <div className="text-xs text-gray-500">
                  {promotionRuleSummary({ ...promotionPricing.applied_promotion, selling_unit_of_measure: group.sellingUnit })}
                </div>
              ) : null}
            </div>
          ) : null}
        </td>
        <td className="px-2 py-2 align-top">
          <SummaryDiscountInput
            groupKey={group.key}
            productLabel={productLabel}
            sourceItems={group.sourceItems}
            disabled={!canEditSummaryDiscount}
            onCommit={onUpdateSummaryDiscount}
          />
        </td>
        {isManagement ? (
          <>
            <td className="px-2 py-2 text-right align-top">
              <div className="inline-flex items-center justify-end gap-2">
                <span
                  className="inline-flex min-w-16 items-center justify-end rounded-md border border-gray-200 bg-gray-50 px-2 py-1 tabular-nums"
                  aria-label={`Quantity at the base price for ${productLabel}`}
                >
                  {formatQuantity(baseQuantity, group.sellingUnit)}
                </span>
                <span className="text-xs text-gray-500">{unitLabel}</span>
                {canEdit ? (
                  <OrderPricingNumberInput
                    ariaLabel={`Unit price for ${productLabel}`}
                    disabled={saving}
                    onBlur={() => onFieldBlur(group)}
                    onChange={onUnitPriceChange}
                    onKeyDown={(event) => onFieldKeyDown(event, group)}
                    value={editor.unitPrice}
                  />
                ) : (
                  <span className="w-24 text-right tabular-nums">
                    {group.baseUnitPrice == null
                      ? "—"
                      : convertToCurrencyFormat(group.baseUnitPrice, 2)}
                  </span>
                )}
              </div>
              {group.promotionPricing ? (
                <div className="mt-1 text-xs text-gray-600" aria-label="Saved base unit price">
                  Base Price{group.promotionPricing.base_prices_differ ? " avg." : ""} {group.promotionPricing.average_base_unit_price == null
                    ? "—"
                    : convertToCurrencyFormat(group.promotionPricing.average_base_unit_price, 2)}
                </div>
              ) : null}
            </td>
            <td className="px-2 py-2 text-right align-top tabular-nums">
              {convertToCurrencyFormat(subtotal, 2)}
            </td>
          </>
        ) : null}
        <td className="px-2 py-2 align-middle">
          <SummaryTaxIncludedCheckbox
            compact
            group={group}
            productLabel={productLabel}
            disabled={!canEditSummaryTaxIncluded || taxSaveStatus === "saving"}
            onCommit={onBulkUpdateSourceItemTaxIncluded}
          />
        </td>
        {isManagement ? (
          <>
            <td className="px-2 py-2 align-top">
              <TaxRatesCell
                canEditTaxes={canEditTaxes}
                group={group}
                onCommit={onSaveTaxes}
                productLabel={productLabel}
                saveStatus={taxSaveStatus}
                taxRateOptions={taxRateOptions}
              />
            </td>
          </>
        ) : null}
        {showActions ? (
          <td className="px-2 py-2 text-center align-top">
            <div className="inline-flex items-center justify-center gap-1">
              {canEdit && group.totalQuantity > 0 ? (
                <button
                  type="button"
                  onClick={onAddTier}
                  className="inline-flex items-center justify-center rounded p-1 text-main-purple hover:bg-indigo-50 hover:text-indigo-700"
                  title="Add price tier"
                  aria-label={`Add price tier for ${productLabel}`}
                >
                  <PlusIcon className="h-5 w-5" />
                </button>
              ) : null}
              {canEdit ? <CellSaveStatusIndicator status={saveStatus} /> : null}
            </div>
          </td>
        ) : null}
      </tr>
      {isManagement
        ? editor.tiers.map((tier, index) => (
        <tr key={`${group.key}-tier-${index}`} className="bg-gray-50">
          <td className="px-2 py-2 pl-6 text-gray-500" colSpan={4}>
            Split
          </td>
          <td />
          {isManagement ? (
            <>
              <td className="px-2 py-2 text-right">
                <div className="inline-flex items-center justify-end gap-2">
                  {canEdit ? (
                    <input
                      id={`tier-quantity-${group.key}-${index}`}
                      aria-label={`Quantity for additional price tier ${
                        index + 1
                      } of ${productLabel}`}
                      className="w-20 rounded-md border border-gray-300 px-2 py-1 text-right text-sm tabular-nums focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 disabled:bg-gray-50"
                      disabled={saving}
                      inputMode="decimal"
                      onBlur={() => onFieldBlur(group)}
                      onChange={(event) =>
                        onTierChange(index, "quantity", event.target.value)
                      }
                      onKeyDown={(event) => onFieldKeyDown(event, group)}
                      type="text"
                      value={tier.quantity}
                    />
                  ) : (
                    <span className="w-20 text-right tabular-nums">
                      {tier.quantity}
                    </span>
                  )}
                  <span className="text-xs text-gray-500">{unitLabel}</span>
                  {canEdit ? (
                    <OrderPricingNumberInput
                      ariaLabel={`Unit price for additional price tier ${
                        index + 1
                      } of ${productLabel}`}
                      disabled={saving}
                      onBlur={() => onFieldBlur(group)}
                      onChange={(nextPrice) =>
                        onTierChange(index, "unitPrice", nextPrice)
                      }
                      onKeyDown={(event) => onFieldKeyDown(event, group)}
                      value={tier.unitPrice}
                    />
                  ) : (
                    <span className="w-24 text-right tabular-nums">
                      {convertToCurrencyFormat(tier.unitPrice, 2)}
                    </span>
                  )}
                </div>
              </td>
              <td />
              <td />
              <td />
            </>
          ) : null}
          {showActions ? (
            <td className="px-2 py-2 text-center">
              {canEdit ? <button
                type="button"
                onClick={() => onRemoveTier(index)}
                className="inline-flex items-center justify-center rounded p-1 text-red-600 hover:bg-red-50 hover:text-red-700"
                title="Remove price tier"
                aria-label={`Remove price tier ${index + 1} for ${productLabel}`}
              >
                <TrashIcon className="h-5 w-5" />
              </button> : null}
            </td>
          ) : null}
        </tr>
      ))
        : null}
    </>
  );
}

function OrderTotals({ values, groups, isManagement }) {
  if (!values) {
    return null;
  }

  const includedTax = Math.round(groups.reduce(
    (sum, group) => sum + group.sourceItems.reduce(
      (amount, item) => amount + (item.tax_included ? Number(item.tax_amount || 0) : 0),
      0,
    ),
    0,
  ) * 100) / 100;
  const discountedItemIds = new Set();
  const totalDiscountCents = groups.reduce((sum, group) => group.sourceItems.reduce((amount, item) => {
    const itemId = item.id == null ? null : String(item.id);
    if (itemId !== null && discountedItemIds.has(itemId)) return amount;
    if (itemId !== null) discountedItemIds.add(itemId);
    const discount = Number(item.discount_amount || 0);
    return amount + (Number.isFinite(discount) && discount > 0 ? Math.round(discount * 100) : 0);
  }, sum), 0);
  const totalDiscounts = totalDiscountCents / 100;
  const subtotalBeforeDiscounts = Number(values.subtotal_dollar_amount || 0) + totalDiscounts;
  const taxes = Number(values.tax_amount || 0);

  return (
    <div className="mt-6 flex justify-end">
      <div className="w-full max-w-sm">
        <div className="space-y-2">
          {isManagement ? (
            <>
              <div className="flex justify-between items-center">
                <span className="text-md font-medium text-gray-600">
                  Subtotal:
                </span>
                <span className="text-md font-semibold text-gray-900">
                  {convertToCurrencyFormat(subtotalBeforeDiscounts, 2)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-md font-medium text-gray-600">Discounts:</span>
                <span className="text-md font-semibold text-gray-900">
                  {convertToCurrencyFormat(totalDiscounts > 0 ? -totalDiscounts : 0, 2)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-md font-medium text-gray-600">Taxes:</span>
                <span className="text-md font-semibold text-gray-900">
                  {convertToCurrencyFormat(taxes, 2)}
                </span>
              </div>
              <div className="border-t border-gray-300 pt-2 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-gray-900">Total:</span>
                  <span className="text-lg font-bold text-gray-900">
                    {convertToCurrencyFormat(values.total_dollar_amount, 2)}
                  </span>
                </div>
              </div>
              {includedTax > 0 ? (
                <div className="flex justify-between items-center text-sm text-gray-600">
                  <span>Tax included in price:</span>
                  <span className="tabular-nums">{convertToCurrencyFormat(includedTax, 2)}</span>
                </div>
              ) : null}
            </>
          ) : null}
          <div className="flex justify-between items-center">
            <span className="text-lg font-bold text-gray-900">
              Total Cartons:
            </span>
            <span className="text-lg font-bold text-gray-900">
              {values.total_cartons} cartons
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
